/**
 * App Component
 * =============
 * Main application with routing setup.
 * All routes require authentication - redirects to Microsoft login if not authenticated.
 */

import { BrowserRouter, Routes, Route } from 'react-router-dom'
import AuthGuard from '@/components/AuthGuard'
import Layout from '@/components/Layout'
import Dashboard from '@/pages/Dashboard'
import ProductList from '@/pages/ProductList'
import ProductView from '@/pages/ProductView'
import ProductForm from '@/pages/ProductForm'
import Categories from '@/pages/Categories'
import CategoryProducts from '@/pages/CategoryProducts'
import Locations from '@/pages/Locations'
import Manufacturers from '@/pages/Manufacturers'
import ManufacturerProducts from '@/pages/ManufacturerProducts'
import Images from '@/pages/Images'
import MotoLocations from '@/pages/MotoLocations'
import MotoManufacturers from '@/pages/MotoManufacturers'
import MotoVendors from '@/pages/MotoVendors'
import MotorcyclesDashboard from '@/pages/MotorcyclesDashboard'
import BikeInformation from '@/pages/motorcycles/BikeInformation'
import BikeHistory from '@/pages/motorcycles/BikeHistory'
import BikeService from '@/pages/motorcycles/BikeService'
import BikeBuild from '@/pages/motorcycles/BikeBuild'
import BikeParts from '@/pages/motorcycles/BikeParts'
import BikePartsSearch from '@/pages/motorcycles/BikePartsSearch'
import BikeForm from '@/pages/motorcycles/BikeForm'
import AssetLocations from '@/pages/AssetLocations'
import AssetManufacturers from '@/pages/AssetManufacturers'
import AssetList from '@/pages/AssetList'
import AssetForm from '@/pages/AssetForm'
import AssetView from '@/pages/AssetView'
import AssetDashboard from '@/pages/AssetDashboard'
import ProxmoxList from '@/pages/ProxmoxList'
import SynologyList from '@/pages/SynologyList'
import UniFiList from '@/pages/UniFiList'
import DiskHealthPage from '@/pages/DiskHealthPage'
import TicketList from '@/pages/TicketList'
import TicketView from '@/pages/TicketView'
import TicketForm from '@/pages/TicketForm'
import TimelogPage from '@/pages/TimelogPage'
import About from '@/pages/About'

function NotFound() {
  console.log('🚀 Exodus v1.18.2 - Built from git.asgeir.is (Forgejo CI/CD)')
  return (
    <div className="text-center py-12">
      <h1 className="text-4xl font-bold text-gray-900 mb-4">404</h1>
      <p className="text-gray-500">Page not found</p>
      <a href="/" className="text-primary-600 hover:underline mt-4 inline-block">
        Go back home
      </a>
    </div>
  )
}

export default function App() {
  console.log('🚀 Exodus v1.18.2 - Built from git.asgeir.is (Forgejo CI/CD)')
  return (
    <BrowserRouter>
      <AuthGuard>
        <Routes>
          <Route path="/" element={<Layout />}>
            <Route index element={<Dashboard />} />
            <Route path="products" element={<ProductList />} />
            <Route path="products/new" element={<ProductForm />} />
            <Route path="products/:productId" element={<ProductView />} />
            <Route path="products/:productId/edit" element={<ProductForm />} />
            <Route path="categories" element={<Categories />} />
            <Route path="categories/:categoryId/products" element={<CategoryProducts />} />
            <Route path="locations" element={<Locations />} />
            <Route path="manufacturers" element={<Manufacturers />} />
            <Route path="manufacturers/:manufacturerId/products" element={<ManufacturerProducts />} />
            <Route path="images" element={<Images />} />
            {/* Motorcycle module routes */}
            <Route path="motorcycles" element={<MotorcyclesDashboard />} />
            <Route path="motorcycles/new" element={<BikeForm />} />
            <Route path="motorcycles/:bikeId/information" element={<BikeInformation />} />
            <Route path="motorcycles/:bikeId/history" element={<BikeHistory />} />
            <Route path="motorcycles/:bikeId/service" element={<BikeService />} />
            <Route path="motorcycles/:bikeId/build" element={<BikeBuild />} />
            <Route path="motorcycles/:bikeId/parts" element={<BikeParts />} />
            <Route path="motorcycles/:bikeId/search" element={<BikePartsSearch />} />
            <Route path="motorcycles/:bikeId/edit" element={<BikeForm />} />
            <Route path="moto/locations" element={<MotoLocations />} />
            <Route path="moto/manufacturers" element={<MotoManufacturers />} />
            <Route path="moto/vendors" element={<MotoVendors />} />
            {/* IT Asset Management routes */}
            <Route path="assets" element={<AssetDashboard />} />
            <Route path="assets/list" element={<AssetList />} />
            <Route path="assets/proxmox" element={<ProxmoxList />} />
            <Route path="assets/synology" element={<SynologyList />} />
            <Route path="assets/unifi" element={<UniFiList />} />
            <Route path="assets/disk-health" element={<DiskHealthPage />} />
            <Route path="assets/new" element={<AssetForm />} />
            <Route path="assets/:assetId" element={<AssetView />} />
            <Route path="assets/:assetId/edit" element={<AssetForm />} />
            <Route path="assets/locations" element={<AssetLocations />} />
            <Route path="assets/manufacturers" element={<AssetManufacturers />} />
            {/* Ticketing system routes */}
            <Route path="tickets" element={<TicketList />} />
            <Route path="tickets/new" element={<TicketForm />} />
            <Route path="tickets/:ticketId" element={<TicketView />} />
            <Route path="tickets/:ticketId/edit" element={<TicketForm />} />
            {/* Timelog (time tracking / billing) */}
            <Route path="timelog" element={<TimelogPage />} />
            {/* About / Documentation */}
            <Route path="about" element={<About />} />
            <Route path="*" element={<NotFound />} />
          </Route>
        </Routes>
      </AuthGuard>
    </BrowserRouter>
  )
}
